/**
 * `/skill-name` commands typed into the chat composer.
 *
 * Picking one from the `/` menu leaves the token in the message text. The chat
 * routes scan every user turn for these tokens (`findInvokedSkillCommands`) and
 * force the matching container skill onto the request, regardless of keyword
 * routing — and keep it for the rest of the conversation, since container
 * skills are fixed per request and a follow-up like "Continue" carries no
 * keywords of its own.
 *
 * Client-safe: names and descriptions only. Skill IDs live server-side in
 * `src/app/api/container-skills.ts`, keyed by these same names.
 */

export type SkillCommand = {
  name: string;
  description: string;
};

export const SKILL_COMMANDS: readonly SkillCommand[] = [
  { name: "woods-design", description: "Woods brand for pages, artifacts and emails" },
  { name: "woods-docx", description: "Woods-branded Word documents and templates" },
  { name: "woods-meeting-notes-docx", description: "Meeting notes as a Woods Word document" },
  { name: "woods-ai-policy", description: "Woods AI Policy and Usage Guidelines" },
  { name: "docx", description: "Read and edit Word documents" },
  { name: "pdf", description: "Read, fill and build PDF files" },
  { name: "xlsx", description: "Read and build Excel spreadsheets" },
  { name: "pptx", description: "Read and build PowerPoint decks" },
];

export type SkillCommandQuery = {
  query: string;
  start: number;
  end: number;
};

// Same shape as the @-mention trigger: "/" at the start of the text or after
// whitespace, so URLs and paths ("https://", "src/app") never open the menu.
const SKILL_QUERY_PATTERN = /(?:^|\s)\/([a-z0-9-]*)$/i;

/** Finds an in-progress `/skill` query ending exactly at `caret`. */
export function findSkillCommandQuery(value: string, caret: number): SkillCommandQuery | null {
  const match = value.slice(0, caret).match(SKILL_QUERY_PATTERN);
  if (!match) return null;

  const query = match[1];
  return { query, start: caret - query.length - 1, end: caret };
}

/** Case-insensitive match against the name, prefix matches first. */
export function filterSkillCommands(query: string): SkillCommand[] {
  const q = query.toLowerCase();
  const prefix = SKILL_COMMANDS.filter((skill) => skill.name.startsWith(q));
  const contains = SKILL_COMMANDS.filter((skill) => !skill.name.startsWith(q) && skill.name.includes(q));
  return [...prefix, ...contains];
}

export type SkillCommandRange = {
  name: string;
  start: number;
  end: number;
};

/**
 * Every recognised `/skill-name` token in `text`, with its position. Shared by
 * the composer highlight and the server-side routing so both agree on which
 * tokens count.
 */
export function findSkillCommandRanges(text: string): SkillCommandRange[] {
  const names = new Set(SKILL_COMMANDS.map((skill) => skill.name));
  const ranges: SkillCommandRange[] = [];
  // Lookahead allows trailing punctuation ("use /woods-design.") but not a
  // longer name ("/pdf" must not match inside "/pdf-tools").
  for (const match of text.matchAll(/(^|\s)\/([a-z0-9-]+)(?=$|[\s.,;:!?)])/gi)) {
    const name = match[2].toLowerCase();
    if (!names.has(name)) continue;
    const start = match.index + match[1].length;
    ranges.push({ name, start, end: start + name.length + 1 });
  }
  return ranges;
}

/** Names of every `/skill-name` command present in `text`, in order of appearance. */
export function findInvokedSkillCommands(text: string): string[] {
  return [...new Set(findSkillCommandRanges(text).map((range) => range.name))];
}

// A wider display-only separator leaves room for a padded inline pill — an en
// space (0.5em) less the pill's padding reads as an ordinary word gap. Keep one
// character per space so textarea selection offsets still match the input.
const PILL_SEPARATOR = "\u2002";

/** Widens the single space on either side of each pill range, for display only. */
export function formatPillSeparators(text: string, ranges: readonly { start: number; end: number }[]): string {
  const characters = text.split("");
  for (const range of ranges) {
    for (const index of [range.start - 1, range.end]) {
      if (characters[index] === " ") characters[index] = PILL_SEPARATOR;
    }
  }
  return characters.join("");
}

export function formatSkillCommandInput(text: string): string {
  return formatPillSeparators(text, findSkillCommandRanges(text));
}

export function parseSkillCommandInput(text: string): string {
  // Restore separators even if the command was edited or deleted this event.
  return text.replaceAll(PILL_SEPARATOR, " ");
}
